/**
 * UstvaExportPanel — Export der UStVA-Vorschau als ELSTER-Nutzdaten-XML (M-U10).
 *
 * Geschwister-Panel UNTER `<VatReturnView/>` (nur gerendert, wenn `canExportUstvaXml`).
 * Verantwortung:
 *   - fail-closed Gate-Anzeige (Blocker > Warn-Checkbox > Info), §8.1.
 *   - Inline-Bundesland-Select bei fehlendem `sellerData.bundesland` (persistiert via
 *     `saveSellerData`, additives optionales Feld).
 *   - Anzahlungs-Block (B9): die reine In-Memory-Detektion läuft AUF DER PAGE (§7.5,
 *     Rev. 3) und kommt als `downPaymentInfo`-Prop herein — KEINE Firestore-Probe mehr.
 *   - Export-Button (Payload-Mapper + `generateUstvaXml`) mit Lade-/Fehlerzustand.
 *   - Erfolgs-Anleitung (3 Schritte + Prüf-Hinweis + Mein-ELSTER-Link).
 *
 * KEINE Steuerlogik — Gate + Mapper leben pur in `utils/ustvaExport.ts`. Alle nutzer-
 * sichtbaren Texte kommen aus i18n (`ustva.json`, Namespace `export.*`, G-U3-freigegeben).
 *
 * @module UstvaExportPanel
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IconWarning, IconInfo, IconDownload, IconExternalLink, IconCheck } from '../../icons';
import { useAuth } from '../../context/AuthContext';
import logger from '../../utils/logger';
import type { Seller } from '../../types/invoiceTypes';
import type { VatReturnFigures } from '../../utils/vatReturn';
import type { SummaryPeriod } from '../../utils/expenseSummary';
import {
  evaluateUstvaExportGate,
  toUstvaXmlRequest,
  BUNDESLAND_CODES,
  type BundeslandCode,
  type DownPaymentDetection,
  type UstvaGateBlocker,
  type UstvaGateWarning,
} from '../../utils/ustvaExport';
import { generateUstvaXml, type UstvaXmlResult } from '../../api/ustvaXmlApi';

// ─── Geld-Anzeige (abgeschnitten Richtung 0 — identisch zu VatReturnView.fmtBase) ──

const euroFullFmt = new Intl.NumberFormat('de-DE', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

function fmtBase(cents: number): string {
  return euroFullFmt.format(Math.trunc(cents / 100));
}

export interface UstvaExportPanelProps {
  /** Aggregierte Kennzahlen (dieselben wie an `VatReturnView`). */
  figures: VatReturnFigures;
  /** Stammdaten des aktuellen Nutzers (nicht-null im Preview-Pfad). */
  seller: Seller;
  /** Aufgelöster Zeitraum (Monat/Quartal/Jahr). */
  period: SummaryPeriod;
  /** Belege ohne BT-72-Leistungsdatum in der Periode (W2). */
  fallbackDateCount: number;
  /** G-3: perioden-gefilterte, geladene Ausgaben (reiner Anzeige-Input, KEIN Gate-Input). */
  periodExpenseCount: number;
  /**
   * Ergebnis der reinen In-Memory-Anzahlungs-Detektion (§7.5, Rev. 3), berechnet AUF DER
   * PAGE über denselben geladenen `invoices`-Datensatz wie `buildVatReturn`. `present`
   * steuert B9; `matchCount`/`firstMatchInvoiceNumber` speisen den Blocker-Detailtext.
   */
  downPaymentInfo: DownPaymentDetection;
}

/**
 * Rendert das Export-Panel unter der UStVA-Vorschau. Wertet die Gate-Matrix synchron
 * aus (kein IO — die Anzahlungs-Detektion kommt als Prop) und triggert bei Freigabe den
 * XML-Download.
 */
const UstvaExportPanel: React.FC<UstvaExportPanelProps> = ({
  figures,
  seller,
  period,
  fallbackDateCount,
  periodExpenseCount,
  downPaymentInfo,
}) => {
  const { t } = useTranslation('ustva');
  const { saveSellerData } = useAuth();

  const [warnAcknowledged, setWarnAcknowledged] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [result, setResult] = useState<UstvaXmlResult | null>(null);
  const [bundeslandDraft, setBundeslandDraft] = useState<BundeslandCode | ''>('');
  const [savingBundesland, setSavingBundesland] = useState(false);
  const [bundeslandError, setBundeslandError] = useState(false);

  // ── Gate-Auswertung (rein/synchron — B9 aus dem Page-Detektor, §7.5) ─────────────
  const gate = useMemo(
    () =>
      evaluateUstvaExportGate(
        figures,
        seller.taxationMode,
        period,
        fallbackDateCount,
        seller,
        downPaymentInfo.present,
      ),
    [figures, seller, period, fallbackDateCount, downPaymentInfo.present],
  );

  // Nach Änderung des Gate-Ergebnisses die Warn-Bestätigung zurücksetzen, falls kein Warn.
  useEffect(() => {
    if (gate.status !== 'warn') setWarnAcknowledged(false);
  }, [gate.status]);

  // ── Nullmeldungs-/„0-Belege"-Info (§8.1 INFO, G-3) ──────────────────────────────
  const isNullReport = useMemo(
    () =>
      figures.kz81BaseCents === 0 &&
      figures.kz86BaseCents === 0 &&
      figures.kz35BaseCents === 0 &&
      figures.kz36VatCents === 0 &&
      figures.kz41BaseCents === 0 &&
      figures.kz43BaseCents === 0 &&
      figures.kz48BaseCents === 0 &&
      figures.kz60BaseCents === 0 &&
      figures.kz84BaseCents === 0 &&
      figures.kz85VatCents === 0 &&
      figures.kz89BaseCents === 0 &&
      figures.kz89VatCents === 0 &&
      figures.kz66VatCents === 0 &&
      figures.kz61VatCents === 0 &&
      figures.kz67VatCents === 0 &&
      figures.kz83PayableCents === 0,
    [figures],
  );
  const isZeroDocsLoaded = figures.includedIncomeCount === 0 && periodExpenseCount === 0;

  // ── Bundesland-Select-Handler ────────────────────────────────────────────────────
  const handleSaveBundesland = async (): Promise<void> => {
    if (bundeslandDraft === '') return;
    setSavingBundesland(true);
    setBundeslandError(false);
    try {
      await saveSellerData({ bundesland: bundeslandDraft });
    } catch (err) {
      logger.error('UstvaExportPanel: saving bundesland failed', err);
      setBundeslandError(true);
    } finally {
      setSavingBundesland(false);
    }
  };

  // ── Export-Handler ───────────────────────────────────────────────────────────────
  const handleExport = async (): Promise<void> => {
    if (gate.status === 'blocked') return;
    if (gate.status === 'warn' && !warnAcknowledged) return;
    setExporting(true);
    setResult(null);
    try {
      const request = toUstvaXmlRequest(
        figures,
        seller,
        period,
        fallbackDateCount,
        downPaymentInfo.present, // per Aufrufer-Vertrag hier stets false (B9 sonst geblockt)
        gate.status === 'warn' ? warnAcknowledged : false,
      );
      const res = await generateUstvaXml(request);
      setResult(res);
    } catch (err) {
      logger.error('UstvaExportPanel: export failed unexpectedly', err);
      setResult({ kind: 'network_error' });
    } finally {
      setExporting(false);
    }
  };

  // ── Render-Helfer ────────────────────────────────────────────────────────────────
  const renderBlocker = (b: UstvaGateBlocker): React.ReactNode => {
    const bundeslandMissing = seller.bundesland == null;
    // B9 (§7.5): count + erste Rechnungsnummer kommen aus dem Page-Detektor, nicht aus
    // dem Blocker-Objekt (das Gate kennt nur den booleschen `present`).
    const interpolation =
      b.code === 'down_payments'
        ? { count: downPaymentInfo.matchCount }
        : b.count != null
          ? { count: b.count, amount: fmtBase(b.amountCents ?? 0) }
          : undefined;
    const showDownPaymentDetail =
      b.code === 'down_payments' && downPaymentInfo.firstMatchInvoiceNumber != null;
    return (
      <li key={b.code} className="flex items-start gap-2">
        <span className="text-[var(--tone-danger-fg)] mt-0.5">
          <IconWarning size={14} />
        </span>
        <div className="text-sm text-[var(--tone-danger-fg)]">
          <span>{t(`export.blockers.${blockerKey(b.code)}`, interpolation)}</span>
          {showDownPaymentDetail && (
            <div className="mt-1 text-xs opacity-90">
              {t('export.blockers.downPaymentsDetail', {
                invoiceNumber: downPaymentInfo.firstMatchInvoiceNumber,
              })}
            </div>
          )}
          {b.code === 'missing_master_data' && bundeslandMissing && (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <select
                aria-label={t('export.bundeslandSelect.label')}
                value={bundeslandDraft}
                onChange={(e) => setBundeslandDraft(e.target.value as BundeslandCode | '')}
                className="rounded border border-hairline bg-surface-raised px-2 py-1 text-sm text-primary"
              >
                <option value="">{t('export.bundeslandSelect.placeholder')}</option>
                {BUNDESLAND_CODES.map((code) => (
                  <option key={code} value={code}>
                    {t(`export.bundeslandSelect.options.${code}`)}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={handleSaveBundesland}
                disabled={bundeslandDraft === '' || savingBundesland}
                className="rounded bg-indigo-600 hover:bg-indigo-500 px-3 py-1 text-sm text-white disabled:opacity-50"
              >
                {savingBundesland
                  ? t('export.bundeslandSelect.saving')
                  : t('export.bundeslandSelect.save')}
              </button>
              {bundeslandError && (
                <span className="text-xs text-[var(--tone-danger-fg)]">
                  {t('export.bundeslandSelect.error')}
                </span>
              )}
            </div>
          )}
        </div>
      </li>
    );
  };

  const renderWarning = (w: UstvaGateWarning): React.ReactNode => (
    <li key={w.code} className="flex items-start gap-2">
      <span className="text-[var(--tone-warning-fg)] mt-0.5">
        <IconWarning size={14} />
      </span>
      <span className="text-sm text-[var(--tone-warning-fg)]">
        {t(`export.warnings.${warningKey(w.code)}`, w.count != null ? { count: w.count } : undefined)}
      </span>
    </li>
  );

  // ── Layout ───────────────────────────────────────────────────────────────────────
  return (
    <div className="mt-6 rounded-lg border border-hairline bg-surface-raised p-4 space-y-4">
      <h2 className="text-base font-semibold text-primary flex items-center gap-2">
        <IconDownload size={16} className="text-accent-ink" />
        {t('export.title')}
      </h2>

      {/* Export-spezifischer Disclaimer (G-U3, additiv zum Vorschau-Disclaimer). */}
      <div className="rounded-lg border border-[var(--tone-warning-border)] bg-[var(--tone-warning-bg)] p-3 text-sm text-[var(--tone-warning-fg)]">
        <p className="font-semibold">{t('export.disclaimer.title')}</p>
        <p className="mt-1">{t('export.disclaimer.body')}</p>
        <p className="mt-1">{t('export.disclaimer.responsibility')}</p>
      </div>

      {/* Blocker-Liste (keine Checkbox, Button disabled). */}
      {gate.status === 'blocked' && (
        <div className="rounded-lg border border-[var(--tone-danger-border)] bg-[var(--tone-danger-bg)] p-3">
          <p className="text-sm font-semibold text-[var(--tone-danger-fg)] mb-2">
            {t('export.blockedTitle')}
          </p>
          <ul className="space-y-2">{gate.blockers.map(renderBlocker)}</ul>
        </div>
      )}

      {/* Warn-Liste + Bestätigungs-Checkbox. */}
      {gate.status === 'warn' && (
        <div className="rounded-lg border border-[var(--tone-warning-border)] bg-[var(--tone-warning-bg)] p-3 space-y-3">
          <ul className="space-y-2">{gate.warnings.map(renderWarning)}</ul>
          <label className="flex items-start gap-2 text-sm text-[var(--tone-warning-fg)] cursor-pointer">
            <input
              type="checkbox"
              checked={warnAcknowledged}
              onChange={(e) => setWarnAcknowledged(e.target.checked)}
              className="mt-0.5"
            />
            <span>{t('export.warnings.acknowledge')}</span>
          </label>
        </div>
      )}

      {/* Info: Nullmeldung / 0 Belege geladen (nur wenn nicht geblockt). */}
      {gate.status !== 'blocked' && (isNullReport || isZeroDocsLoaded) && (
        <div className="rounded-lg border border-hairline bg-surface p-3 text-sm text-secondary flex items-start gap-2">
          <span className="text-accent-ink mt-0.5">
            <IconInfo size={14} />
          </span>
          <span>{t(isZeroDocsLoaded ? 'export.info.zeroDocsLoaded' : 'export.info.nullReport')}</span>
        </div>
      )}

      {/* Export-Button. */}
      <button
        type="button"
        onClick={handleExport}
        disabled={
          gate.status === 'blocked' ||
          exporting ||
          (gate.status === 'warn' && !warnAcknowledged)
        }
        className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
      >
        <IconDownload size={16} />
        {exporting ? t('export.exporting') : t('export.button')}
      </button>

      {/* Ergebnis-/Fehleranzeige. */}
      {result && <ExportResult result={result} t={t} />}
    </div>
  );
};

// ─── Ergebnis-Anzeige (Erfolg-Anleitung / Fehlerliste) ───────────────────────────

const ExportResult: React.FC<{
  result: UstvaXmlResult;
  t: (k: string, opts?: Record<string, unknown>) => string;
}> = ({ result, t }) => {
  if (result.kind === 'success') {
    return (
      <div className="rounded-lg border border-[var(--tone-success-border)] bg-[var(--tone-success-bg)] p-4 space-y-3">
        <p className="text-sm font-semibold text-[var(--tone-success-fg)] flex items-center gap-2">
          <IconCheck size={16} />
          {t('export.instructions.title')}
        </p>
        <ol className="list-decimal list-inside space-y-1 text-sm text-primary">
          <li>{t('export.instructions.step1')}</li>
          <li>{t('export.instructions.step2')}</li>
          <li>{t('export.instructions.step3')}</li>
        </ol>
        <p className="text-sm font-medium text-primary">{t('export.instructions.checkAllValues')}</p>
        {result.translit && (
          <p className="text-sm text-[var(--tone-warning-fg)] flex items-start gap-2">
            <span className="mt-0.5">
              <IconWarning size={14} />
            </span>
            <span>{t('export.info.translitReplaced')}</span>
          </p>
        )}
        <a
          href="https://www.elster.de"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-sm text-accent-ink underline"
        >
          <IconExternalLink size={14} />
          {t('export.instructions.elsterLink')}
        </a>
      </div>
    );
  }

  if (result.kind === 'validation_error') {
    return (
      <div className="rounded-lg border border-[var(--tone-danger-border)] bg-[var(--tone-danger-bg)] p-3">
        <p className="text-sm font-semibold text-[var(--tone-danger-fg)] mb-2">
          {t('export.serverError.title')}
        </p>
        <ul className="space-y-1">
          {result.errors.map((e, i) => (
            <li key={`${e.code}-${i}`} className="text-sm text-[var(--tone-danger-fg)]">
              {t(`export.errors.${e.code}`, { defaultValue: t('export.errors.generic') })}
            </li>
          ))}
          {result.errors.length === 0 && (
            <li className="text-sm text-[var(--tone-danger-fg)]">{t('export.errors.generic')}</li>
          )}
        </ul>
      </div>
    );
  }

  const messageKey =
    result.kind === 'auth_error'
      ? 'export.serverError.auth'
      : result.kind === 'rate_limited'
        ? 'export.serverError.rateLimited'
        : result.kind === 'network_error'
          ? 'export.serverError.network'
          : 'export.serverError.generic';

  return (
    <div className="rounded-lg border border-[var(--tone-danger-border)] bg-[var(--tone-danger-bg)] p-3 text-sm text-[var(--tone-danger-fg)]">
      {t(messageKey)}
    </div>
  );
};

// ─── Code → i18n-Key-Mapping (camelCase-Keys unter export.blockers / export.warnings) ──

function blockerKey(code: UstvaGateBlocker['code']): string {
  switch (code) {
    case 'ist_mode':
      return 'istMode';
    case 'small_business':
      return 'smallBusiness';
    case 'year_period':
      return 'yearPeriod';
    case 'unclassified':
      return 'unclassified';
    case 'unclassified_expense':
      return 'unclassifiedExpense';
    case 'reverse_charge_split':
      return 'reverseChargeSplit';
    case 'acquisition_rate_mismatch':
      return 'acquisitionRateMismatch';
    case 'negative_kz':
      return 'negativeKz';
    case 'down_payments':
      return 'downPayments';
    case 'missing_master_data':
      return 'missingMasterData';
    default:
      return 'generic';
  }
}

function warningKey(code: UstvaGateWarning['code']): string {
  switch (code) {
    case 'excluded_drafts':
      return 'excludedDrafts';
    case 'fallback_dates':
      return 'fallbackDates';
    case 'kz22_december':
      return 'kz22December';
    case 'null_report':
      return 'nullReport';
    default:
      return 'generic';
  }
}

export default UstvaExportPanel;
